import { useMemo } from 'react';
import { Props as ReactApexChartProps } from 'react-apexcharts';
import type { ExternalMonitoringPeriodBounds } from '../../../../../../helpers/external-monitoring-period';
import { ChartProps } from '../../dashboard-components/dashboard-charts/use-dashboard-charts';
import { type ExternalMonitoringLanguageAggregation } from '../language-aggregation';

export const useNegativeByLanguageBar = (
  _period?: ExternalMonitoringPeriodBounds,
  aggregation?: ExternalMonitoringLanguageAggregation | null,
): ChartProps => {
  const chartData = useMemo<ReactApexChartProps>(() => {
    const items = aggregation?.items ?? [];
    const languages = items.map((x) => x.languageLabel);
    const totals = items.map((x) => {
      const pos = x.positiveCount;
      const neu = x.neutralCount;
      const neg = x.negativeCount;
      const sum = pos + neu + neg || 1;
      return { pos: (pos / sum) * 100, neu: (neu / sum) * 100, neg: (neg / sum) * 100 };
    });

    const toInt = (value: number) => Math.round(value);

    return {
      series: [
        { name: 'Позитивные', data: totals.map((t) => toInt(t.pos)) },
        { name: 'Нейтральные', data: totals.map((t) => toInt(t.neu)) },
        { name: 'Негативные', data: totals.map((t) => toInt(t.neg)) },
      ],
      options: {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '60%',
            borderRadius: 4,
          },
        },
        xaxis: { categories: languages },
        colors: ['#28A745', '#6C757D', '#DC3545'],
        dataLabels: {
          enabled: true,
          formatter: (val: number) => `${Math.round(val)}%`,
        },
        yaxis: {
          max: 100,
          labels: {
            formatter: (val: number) => `${Math.round(val)}%`,
          },
        },
        tooltip: {
          y: {
            formatter: (val: number) => `${Math.round(val)}%`,
          },
        },
        legend: { position: 'top' },
      },
    };
  }, [aggregation, _period?.begin, _period?.end]);

  return {
    ...chartData,
    label: 'Тональность по языкам',
    labelTooltip:
      'Отображает процентное соотношение положительных, нейтральных и отрицательных оценок для каждого языка написания отзывов. Помогает определить уровень лояльности разных сегментов аудитории и выявить специфические проблемы в зависимости от региона или языка обращения.',
    labelInfoIcon: true,
    type: 'bar',
  };
};
